import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { alpha, useTheme } from '@mui/material/styles'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useMenuCart } from '@hooks/menu-cart/useMenuCart'
import { useMenuBranding } from './MenuBrandingContext'
import { fCurrency } from '@utils/formatNumber'

/** Solo en browse del menú: `/menu/:slug` (sin subrutas). */
const isMenuBrowsePath = (pathname: string): boolean =>
    /^\/menu\/[^/]+\/?$/.test(pathname)

export function MenuCartStickyBar() {
    const theme = useTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const { slug = '' } = useParams()
    const { pagosOnlineActivos, isFound } = useMenuBranding()
    const { itemCount, subtotal } = useMenuCart(slug)

    const visible =
        isFound &&
        pagosOnlineActivos &&
        isMenuBrowsePath(location.pathname) &&
        itemCount > 0 &&
        Boolean(slug)

    if (!visible) {
        return null
    }

    return (
        <Box
            component="aside"
            sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme.zIndex.appBar,
                px: { xs: 1.5, sm: 2 },
                pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 2 },
                pt: 1,
                pointerEvents: 'none',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{
                    pointerEvents: 'auto',
                    maxWidth: 640,
                    mx: 'auto',
                    px: 2,
                    py: 1.25,
                    borderRadius: 3,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                    boxShadow: `0 -4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}
                >
                    {fCurrency(subtotal)}
                </Typography>

                <Button
                    variant="contained"
                    onClick={() => navigate(`/menu/${slug}/carrito`)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 2.25,
                        py: 1.1,
                        minWidth: 132,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    Carrito
                    <Box
                        component="span"
                        sx={{
                            minWidth: 22,
                            height: 22,
                            px: 0.6,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.common.white, 0.22),
                            color: 'inherit',
                            fontSize: '0.75rem',
                            fontWeight: 800,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                        }}
                    >
                        {itemCount > 99 ? '99+' : itemCount}
                    </Box>
                </Button>
            </Stack>
        </Box>
    )
}
